/**
 * Uzum design-system colour tokens — the single source both halves of the Uzum
 * themes (`uzum.ts` for MUI, `uzumBui.ts` for Backstage UI) are built from.
 *
 * `uzumColors` is the primitive palette (B&W, Blue-black, Brand, Brand Add,
 * Violet, Lavender, Utilitarian), `uzumSemantic` the light/dark pairs the design
 * system names: Background, Container, Actions, Text. Values are copied from the
 * design-system sheets; an opacity in the sheet ("Black 950 (50%)") becomes the
 * rgba of that primitive. Keep the names as the sheets spell them, so a token can
 * be looked up there.
 */

export const uzumColors = {
  black950: '#010101',
  black900: '#232323',
  black0: '#FFFFFF',

  blueBlack900: '#393A44',
  blueBlack700: '#565A69',
  blueBlack600: '#6B6E7C',
  blueBlack300: '#B3BAC5',
  blueBlack200: '#E0E0E5',
  blueBlack100: '#ECECF0',
  blueBlack50: '#F4F4F6',

  brand900: '#5403BE',
  brand800: '#6200E0',
  brand700: '#7000FF',
  brand600: '#7D2BFF',
  brand500: '#8A54FF',
  brand400: '#A47DFF',
  brand300: '#C0A6FF',
  brand100: '#EBE4FF',
  brand50: '#F3F1FB',

  brandAdd400: '#9DED70',
  brandAdd100: '#E0F9C8',
  brandAdd50: '#EAF8DE',

  violet500: '#BFAED3',
  violet300: '#D9D6E9',

  lavender400: '#C9D0FF',
  lavender200: '#E1E5FF',

  red600: '#F33030',
  red400: '#FC6D6D',
  red100: '#FFE9E9',
  red50: '#FFF3F4',

  green600: '#0DBA61',
  green400: '#57CD7F',
} as const;

/** `#RRGGBB` at `opacity` (0–1), as the sheets write "Black 950 (50%)". */
export function alpha(hex: string, opacity: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${opacity})`;
}

const c = uzumColors;

export type UzumMode = 'light' | 'dark';

export type UzumSemantic = {
  background: {
    /** "Main" — the screen background. */
    main: string;
    /** "Bottom sheet" — raised surfaces: cards, dialogs, menus. */
    sheet: string;
    /** "Darkening" — the scrim under a modal. */
    darkening: string;
  };
  container: {
    grey: string;
    pressed: string;
    accent: string;
    positive: string;
    negative: string;
  };
  action: {
    primary: string;
    primaryPressed: string;
    primaryDisabled: string;
    onPrimary: string;
    onPrimaryDisabled: string;
    secondary: string;
    secondaryPressed: string;
    onSecondary: string;
    tertiary: string;
    tertiaryPressed: string;
    controlOff: string;
  };
  text: {
    primary: string;
    secondary: string;
    disabled: string;
    error: string;
    success: string;
    link: string;
    accent: string;
  };
  /** Hairlines between surfaces. Not in the sheets; derived from Blue-black / Black 0. */
  border: {
    subtle: string;
    strong: string;
  };
};

export const uzumSemantic: Record<UzumMode, UzumSemantic> = {
  light: {
    background: {
      main: c.blueBlack50,
      sheet: c.black0,
      darkening: alpha(c.black950, 0.4),
    },
    container: {
      grey: c.blueBlack50,
      pressed: alpha(c.blueBlack600, 0.05),
      accent: c.brand100,
      positive: c.brandAdd50,
      negative: c.red50,
    },
    action: {
      primary: c.brand700,
      primaryPressed: c.brand900,
      primaryDisabled: c.violet300,
      onPrimary: c.black0,
      onPrimaryDisabled: c.black0,
      secondary: c.brand50,
      secondaryPressed: c.brand100,
      onSecondary: c.brand700,
      tertiary: c.blueBlack50,
      tertiaryPressed: c.blueBlack200,
      controlOff: c.blueBlack200,
    },
    text: {
      primary: c.black950,
      secondary: alpha(c.black950, 0.5),
      disabled: alpha(c.black950, 0.3),
      error: c.red600,
      success: c.green600,
      link: c.brand600,
      accent: c.brand700,
    },
    border: {
      subtle: c.blueBlack100,
      strong: c.blueBlack200,
    },
  },
  dark: {
    background: {
      main: c.black950,
      sheet: c.black900,
      darkening: alpha(c.black950, 0.4),
    },
    container: {
      grey: alpha(c.black0, 0.12),
      pressed: alpha(c.black0, 0.08),
      accent: alpha(c.brand400, 0.4),
      positive: alpha(c.brandAdd400, 0.15),
      negative: alpha(c.red400, 0.15),
    },
    action: {
      primary: c.brand600,
      primaryPressed: c.brand800,
      primaryDisabled: alpha(c.brand400, 0.25),
      onPrimary: c.black0,
      onPrimaryDisabled: alpha(c.black0, 0.35),
      secondary: alpha(c.brand400, 0.4),
      secondaryPressed: alpha(c.brand400, 0.56),
      onSecondary: c.black0,
      tertiary: alpha(c.black0, 0.12),
      tertiaryPressed: alpha(c.black0, 0.2),
      controlOff: alpha(c.black0, 0.2),
    },
    text: {
      primary: c.black0,
      secondary: alpha(c.black0, 0.6),
      disabled: alpha(c.black0, 0.35),
      error: c.red400,
      success: c.green400,
      link: c.brand400,
      accent: c.brand400,
    },
    border: {
      subtle: alpha(c.black0, 0.12),
      strong: alpha(c.black0, 0.2),
    },
  },
};

/**
 * Warning has no colour in the design system. This amber is ours, kept only so
 * Backstage's warning states (deprecated entities, alerts) stay distinguishable
 * from errors. Replace it the day the sheets grow a yellow.
 */
export const uzumWarning = {
  main: '#FFB400',
  light: '#FFD873',
  dark: '#C77F00',
} as const;

export const UZUM_FONT =
  "'TT Uzum', Montserrat, Roboto, Helvetica, Arial, sans-serif";
